import { t } from "i18next";
import { displayToast } from "../../../components/app/AppToast";
import { SoundEffect } from "../../../interfaces";
import { isAndroidApp } from "../../../tools/android";
import { isIOSApp, iosPlaySound } from "../../../tools/ios";

/**
 * Converts a Blob to a base64 string
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result);
      } else {
        reject(new Error("Failed to convert blob to base64"));
      }
    };
    reader.onerror = () => {
      const error = reader.error;
      reject(error === null ? new Error("FileReader failed") : error);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Downloads a file using DOM manipulation (web browser only)
 */
function downloadFileWeb(blobUrl: string, filename: string): void {
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
}

/**
 * Shares/saves an image across platforms (iOS native, Android native, or web)
 *
 * - iOS: Saves image directly to Photos library
 * - Android: Saves image to device gallery
 * - Web: Downloads image file via browser download
 *
 * @param blob - The image blob to share/save
 * @param filename - The filename to use (should include extension, e.g., "qr-code.png")
 * @param successMessage - Translated toast shown once the image is saved
 * @returns Promise<boolean> - true if successful, false otherwise
 */
export async function shareImage(
  blob: Blob,
  filename: string,
  successMessage: string
): Promise<boolean> {
  try {
    console.log(
      `[SAVE_IMG] start | isIOS=${isIOSApp()} isAndroid=${isAndroidApp()} blob=${blob.size}b filename=${filename}`
    );

    if (isIOSApp()) {
      // iOS native: save to Photos library
      const { iosSaveImage } = await import("../../../tools/ios");
      const base64 = await blobToBase64(blob);
      const success = await iosSaveImage(base64, filename);

      if (success) {
        iosPlaySound(SoundEffect.SHARE);
        displayToast(successMessage, "success");
      } else {
        displayToast(t("Something went wrong. Please try again."), "error");
      }

      return success;
    }

    if (isAndroidApp()) {
      // Android native: save to gallery
      console.log("[SAVE_IMG] taking ANDROID native path");
      const { androidSaveImage } = await import("../../../tools/android");
      const base64 = await blobToBase64(blob);
      console.log(`[SAVE_IMG] base64 length=${base64.length}`);
      const success = await androidSaveImage(base64, filename);
      console.log(`[SAVE_IMG] androidSaveImage result=${success}`);

      if (success) {
        iosPlaySound(SoundEffect.SHARE); // Works for both iOS and Android
        displayToast(successMessage, "success");
      } else {
        displayToast(t("Something went wrong. Please try again."), "error");
      }

      return success;
    }

    // Web browser: use DOM download
    console.log("[SAVE_IMG] taking WEB fallback path (no native bridge)");
    const blobUrl = URL.createObjectURL(blob);
    downloadFileWeb(blobUrl, filename);
    URL.revokeObjectURL(blobUrl);
    displayToast(successMessage, "success");

    return true;
  } catch (error) {
    console.error("[SAVE_IMG] EXCEPTION:", error);
    displayToast(t("Something went wrong. Please try again."), "error");
    return false;
  }
}
